import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { BehaviorSubject, filter, merge, Subject, switchMap, takeUntil } from "rxjs";

import { ConfirmComponent } from "../../shared/components/confirm/confirm.component";
import { DialogService } from "../../shared/components/dialog/dialog.service";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import {
  SortOrder,
  TableConfig,
} from "../../shared/components/table/table.component";
import { CsvService } from "../../shared/services/csv.service";
import { unlimitedCreditsSubscriptionPlans } from "../../shared/tool-functions/constants";
import { buildCreditInfo } from "../../shared/tool-functions/credit.constants";
import {
  CompanyOfferCard,
  OfferPlanCode,
} from "../../shared/types/offer-upsell";
import { DynamicType } from "../../thematics/models/dynamic.model";
import { Role } from "../../users/models/users.entity";
import { ProfileService } from "../../users/services/profile.service";
import {
  BillingCheckoutResponse,
  BillingRecordSummary,
  BillingStatus,
  Organization,
  OrganizationSubscription,
  OrganizationTransactionParsed,
  SubscriptionChangeEffectiveMode,
  SubscriptionChangeRequestPayload,
  SubscriptionChangeRequestSummary,
  SubscriptionChangeSimulation,
  SubscriptionChangeType,
  SubscriptionPlanCatalogItem,
  Transaction,
  TransactionType,
} from "../models/organizations.entity";
import { SubscriptionPlanMetadata } from "../models/subscription-plan.metadata";
import { OrganizationRepository } from "../repositories/organization.repository";
import { ManagedOfferCheckoutService } from "../services/managed-offer-checkout.service";
import { OrganizationStoreService } from "../services/organization.store.service";
import {
  getCompanyOfferCard,
  getOfferContextSnapshot,
  getRecommendedOfferPlan,
} from "../utils/plan-offer-marketing";

@Component({
  selector: "app-organization-units-history",
  templateUrl: "./organization-units-history.component.html",
  styleUrls: ["./organization-units-history.component.scss"],
})
export class OrganizationUnitsHistoryComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  private readonly planOrder = new Map<OfferPlanCode, number>([
    ["wetalk", 0],
    ["wetalk-plus", 1],
    ["wetalk-coach", 2],
  ]);

  headerColumns: TableConfig<OrganizationTransactionParsed> = {
    date: {
      displayName: "Date",
      transformFunction: (date: Date) => date.toLocaleDateString(),
      sortable: true,
    },
    units: {
      displayName: "Variation",
      styleFunction: (units: string) =>
        (units.includes("+")
          ? "color: #6aa33e; font-weight: 500 "
          : "color: #704c8d; font-weight: 500"),
    },
    restAmount: {
      displayName: "Total disponible",
      style: "font-weight: 500;",
    },
    description: {
      displayName: "Type d'opération",
      sortable: true,
    },
  };

  transactions: OrganizationTransactionParsed[] = [];

  sortOptionChosen$ = new BehaviorSubject<
    SortOrder<OrganizationTransactionParsed> | undefined
  >(undefined);

  organization!: Organization;

  billingStatus: BillingStatus | null = null;

  loadingCatalog = true;

  isSavingOffer = false;

  isPurchasingJourneys = false;

  isSubmittingSubscriptionChange = false;

  managedFormulaNames: string[] = [];

  managedSubscriptionPlanControl = new FormControl("", { nonNullable: true });

  managedSubscriptionStartsAtControl = new FormControl(
    this.managedOfferCheckoutService.getTodayDate(),
    { nonNullable: true }
  );

  managedSubscriptionSeatCountControl = new FormControl(1, {
    nonNullable: true,
  });

  complementaryJourneysQuantityControl = new FormControl(1, {
    nonNullable: true,
  });

  complementaryJourneyTypeControl = new FormControl("Parole en groupe", {
    nonNullable: true,
  });

  private readonly managedFormulaCodes =
    this.managedOfferCheckoutService.getManagedPlanCodes();

  private managedPlansByName: Record<string, SubscriptionPlanCatalogItem> = {};

  constructor(
    private readonly organizationStoreService: OrganizationStoreService,
    private readonly organizationRepository: OrganizationRepository,
    private readonly snackBarService: SnackbarService,
    private readonly router: Router,
    private readonly csvService: CsvService,
    private readonly profileService: ProfileService,
    private readonly managedOfferCheckoutService: ManagedOfferCheckoutService,
    private readonly dialogService: DialogService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.profileService
      .getMyProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    this.organizationRepository
      .getSubscriptionPlans()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plans) => {
          this.managedOfferCheckoutService.setCatalog(plans);
          const managedPlans = this.managedFormulaCodes
            .map((planCode) => plans.find((plan) => plan.slug === planCode))
            .filter((plan): plan is SubscriptionPlanCatalogItem => !!plan);
          this.managedFormulaNames = managedPlans.map((plan) =>
            this.getPlanDisplayName(plan.slug as OfferPlanCode)
          );
          this.managedPlansByName = managedPlans.reduce((acc, plan) => {
            acc[this.getPlanDisplayName(plan.slug as OfferPlanCode)] = plan;
            return acc;
          }, {} as Record<string, SubscriptionPlanCatalogItem>);
          this.loadingCatalog = false;
          this.syncOfferControls();
        },
        error: () => {
          this.loadingCatalog = false;
          this.snackBarService.pushMessage(
            "Le catalogue des offres n'a pas pu être chargé",
            "error"
          );
        },
      });

    this.organizationStoreService
      .getObservable()
      .pipe(
        filter((organization): organization is Organization => !!organization),
        takeUntil(this.destroy$)
      )
      .subscribe((organization) => {
        this.organization = organization;
        this.syncOfferControls();
        this.buildTransactions();
        this.loadBillingStatus(organization.id);
      });

    this.sortOptionChosen$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.organization) {
        this.buildTransactions();
      }
    });

    merge(
      this.managedSubscriptionPlanControl.valueChanges,
      this.managedSubscriptionSeatCountControl.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToCompanyOffers() {
    this.router.navigateByUrl("/offres-entreprises-2026");
  }

  downloadCsv() {
    this.csvService.downloadOrganizationsTransactionsParsed(this.transactions);
  }

  sortTransactions(sortOption?: SortOrder<OrganizationTransactionParsed>) {
    this.sortOptionChosen$.next(sortOption);
  }

  saveManagedOffer() {
    if (!this.organization?.id || !this.canSaveManagedOffer) {
      return;
    }

    if (this.hasActiveManagedOffer && !this.isAdminViewing) {
      this.snackBarService.pushMessage(
        "Utilisez les actions d'évolution d'abonnement disponibles sur cette page.",
        "default"
      );
      return;
    }

    this.isSavingOffer = true;

    if (
      this.hasActiveManagedOffer &&
      this.isAdminViewing &&
      this.selectedManagedPlanVersionId
    ) {
      this.organizationRepository
        .assignSubscription(this.organization.id, {
          planVersionId: this.selectedManagedPlanVersionId,
          startsAt: new Date(
            this.managedSubscriptionStartsAtControl.value
          ).toISOString(),
          seatCount: this.selectedSeatCount,
        })
        .pipe(
          switchMap(() => this.organizationStoreService.reloadOrganization()),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => {
            this.isSavingOffer = false;
            this.snackBarService.pushMessage(
              "L'offre a bien été remplacée en gestion interne",
              "success"
            );
            this.loadBillingStatus(this.organization.id);
          },
          error: () => {
            this.isSavingOffer = false;
            this.snackBarService.pushMessage(
              "Le remplacement interne de l'offre a échoué",
              "error"
            );
          },
        });
      return;
    }

    if (!this.selectedManagedPlanCode) {
      this.isSavingOffer = false;
      return;
    }

    this.organizationRepository
      .createSubscriptionCheckout(this.organization.id, {
        planCode: this.selectedManagedPlanCode,
        seatCount: this.selectedSeatCount,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSavingOffer = false;
          this.handleSubscriptionCheckoutResponse(response);
        },
        error: () => {
          this.isSavingOffer = false;
          this.snackBarService.pushMessage(
            "Le checkout Sellsy n'a pas pu être préparé pour cette offre",
            "error"
          );
        },
      });
  }

  purchaseComplementaryJourneys() {
    if (!this.organization?.id || !this.canPurchaseComplementaryJourneys) {
      return;
    }

    this.isPurchasingJourneys = true;

    this.organizationRepository
      .createComplementaryJourneysCheckout(this.organization.id, {
        quantity: this.selectedComplementaryJourneyQuantity,
        journeyType: this.selectedComplementaryJourneyType,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isPurchasingJourneys = false;
          this.handleComplementaryJourneysCheckoutResponse(response);
        },
        error: () => {
          this.isPurchasingJourneys = false;
          this.snackBarService.pushMessage(
            "Le checkout Sellsy des parcours complémentaires a échoué",
            "error"
          );
        },
      });
  }

  get hasUnlimitedCredits(): boolean {
    return unlimitedCreditsSubscriptionPlans.includes(
      this.organization?.subscriptionPlan?.slug ?? ""
    );
  }

  get subscriptionPlanName(): string {
    return this.getLegacyPlanDisplayName(
      this.organization?.subscriptionPlan?.slug,
      this.organization?.subscriptionPlan?.name
    );
  }

  get creditInfo() {
    return buildCreditInfo(this.organization?.creditCosts, {
      orientationCallOffered: this.hasComplimentaryOrientationCall,
    });
  }

  get hasComplimentaryOrientationCall(): boolean {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    return (
      currentPlanCode === "wetalk" ||
      currentPlanCode === "wetalk-plus" ||
      currentPlanCode === "wetalk-coach"
    );
  }

  get displayedUnits(): number {
    return (
      this.organization?.creditSummary?.currentUnits ??
      this.organization?.units ??
      0
    );
  }

  get recommendedOfferCard(): CompanyOfferCard | undefined {
    const snapshot = getOfferContextSnapshot(this.organization);
    const recommendedPlan = getRecommendedOfferPlan(snapshot.currentPlanCode);
    return recommendedPlan ? getCompanyOfferCard(recommendedPlan) : undefined;
  }

  get currentOfferName(): string {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    const isManagedOfferPlan = !!currentPlanCode && this.managedFormulaCodes.includes(currentPlanCode);

    return isManagedOfferPlan
      ? this.getPlanDisplayName(currentPlanCode)
      : this.getLegacyPlanDisplayName(
          currentPlanCode ?? this.organization?.subscriptionPlan?.slug,
          this.organization?.activeSubscription?.planCode ?? this.organization?.subscriptionPlan?.name
        ) ||
        this.getLegacyPlanDisplayName(
          this.organization?.subscriptionPlan?.slug,
          this.organization?.subscriptionPlan?.name
        ) || "Aucune offre active";
  }

  get shouldShowOfferManagementSection(): boolean {
    const currentPlanCode =
      this.organization?.activeSubscription?.planCode ??
      this.organization?.subscriptionPlan?.slug ??
      "";

    return currentPlanCode !== "personalized-subscription";
  }

  get currentAnnualAmountLabel(): string {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    const seatCount = this.organization?.activeSubscription?.seatCount ?? 0;
    const pricePerSeat =
      this.managedOfferCheckoutService.getAnnualPricePerSeatCents(
        currentPlanCode,
        seatCount
      );

    if (currentPlanCode && pricePerSeat > 0) {
      return this.formatCurrency(pricePerSeat * seatCount);
    }

    const legacyAnnualPriceCents =
      this.currentLegacyAnnualSubscriptionPriceCents;
    if (legacyAnnualPriceCents) {
      return this.formatCurrency(legacyAnnualPriceCents);
    }

    if (!currentPlanCode || pricePerSeat === 0) {
      return "Sans abonnement annuel";
    }

    return "Non disponible";
  }

  get currentComplementaryJourneyPriceLabel(): string {
    const speechPriceCents = this.currentLegacySpeechJourneyPriceCents;
    const coachingPriceCents = this.currentLegacyCoachingJourneyPriceCents;

    if (
      speechPriceCents &&
      coachingPriceCents &&
      speechPriceCents !== coachingPriceCents
    ) {
      return `Parole ${this.formatCurrency(
        speechPriceCents
      )} / Coaching ${this.formatCurrency(coachingPriceCents)}`;
    }

    const price =
      coachingPriceCents ||
      speechPriceCents ||
      this.currentComplementaryJourneyPriceCents;
    return price
      ? `${this.formatCurrency(price)} / parcours`
      : "Non disponible";
  }

  get currentComplementaryJourneyPriceLines(): string[] | null {
    const speechPriceCents = this.currentLegacySpeechJourneyPriceCents;
    const coachingPriceCents = this.currentLegacyCoachingJourneyPriceCents;

    if (
      speechPriceCents &&
      coachingPriceCents &&
      speechPriceCents !== coachingPriceCents
    ) {
      return [
        `Parole à ${this.formatCurrency(speechPriceCents)}`,
        `Coaching à ${this.formatCurrency(coachingPriceCents)}`,
      ];
    }

    return null;
  }

  get shouldChooseComplementaryJourneyType(): boolean {
    return (
      !!this.currentLegacySpeechJourneyPriceCents &&
      !!this.currentLegacyCoachingJourneyPriceCents &&
      this.currentLegacySpeechJourneyPriceCents !==
        this.currentLegacyCoachingJourneyPriceCents
    );
  }

  get currentIncludedJourneysRemaining(): number {
    return this.organization?.creditSummary?.includedJourneysRemaining ?? 0;
  }

  get currentComplementaryJourneysRemaining(): number {
    return (
      this.organization?.creditSummary?.complementaryJourneysRemaining ?? 0
    );
  }

  get hasActiveManagedOffer(): boolean {
    return !!this.organization?.activeSubscription;
  }

  get isAdminViewing(): boolean {
    return this.profileService.profile?.role === Role.Admin;
  }

  get pendingSubscriptionCheckout(): BillingRecordSummary | null {
    return this.billingStatus?.pendingSubscriptionCheckout ?? null;
  }

  get pendingSubscriptionCheckoutUrl(): string | null {
    return this.pendingSubscriptionCheckout?.checkoutUrl ?? null;
  }

  get pendingComplementaryJourneysCheckout(): BillingRecordSummary | null {
    return (
      this.billingStatus?.records.find(
        (record) =>
          record.flowType === "complementary_journeys" &&
          record.status === "payment_pending" &&
          !!record.checkoutUrl
      ) ?? null
    );
  }

  get pendingComplementaryJourneysCheckoutUrl(): string | null {
    return this.pendingComplementaryJourneysCheckout?.checkoutUrl ?? null;
  }

  get pendingComplementaryJourneysLabel(): string {
    const quantity = this.pendingComplementaryJourneysCheckout?.quantity;
    const journeyTypeLabel = this.getJourneyTypeLabel(
      this.pendingComplementaryJourneysCheckout?.journeyType
    );
    if (!quantity) {
      return journeyTypeLabel
        ? `cet achat ${journeyTypeLabel.toLowerCase()}`
        : "cet achat complémentaire";
    }

    if (journeyTypeLabel) {
      return `${quantity} parcours ${journeyTypeLabel.toLowerCase()}`;
    }

    return `${quantity} parcours complémentaire${quantity > 1 ? "s" : ""}`;
  }

  get activeSubscriptionChangeRequest(): SubscriptionChangeRequestSummary | null {
    return this.billingStatus?.activeSubscriptionChangeRequest ?? null;
  }

  get activeSubscriptionChangeRequestCheckoutUrl(): string | null {
    return this.activeSubscriptionChangeRequest?.checkoutUrl ?? null;
  }

  get billingRecords(): BillingRecordSummary[] {
    return this.billingStatus?.records ?? [];
  }

  get isSubscriptionSelectionChangedFromCurrent(): boolean {
    const subscription = this.organization?.activeSubscription;
    if (!subscription) {
      return false;
    }

    const currentPlanCode = subscription.planCode;
    const currentSeatCount = subscription.seatCount ?? 0;

    return (
      (!!this.selectedManagedPlanCode &&
        this.selectedManagedPlanCode !== currentPlanCode) ||
      this.selectedSeatCount !== currentSeatCount
    );
  }

  get latestOfferBillingFeedback(): BillingRecordSummary | null {
    if (
      this.activeSubscriptionChangeRequest ||
      this.isSubscriptionSelectionChangedFromCurrent
    ) {
      return null;
    }

    const offerFlowTypes = [
      "subscription_initial",
      "subscription_renewal",
      "subscription_upgrade",
      "seat_increase",
    ] as const;

    const cancelledRecord = this.billingRecords.find(
      (record) =>
        offerFlowTypes.includes(record.flowType as typeof offerFlowTypes[number]) &&
        record.status === "cancelled",
    );

    if (!cancelledRecord) {
      return null;
    }

    const cancelledRecordIndex = this.billingRecords.indexOf(cancelledRecord);
    const hasNewerOfferActivity = this.billingRecords
      .slice(0, cancelledRecordIndex)
      .some((record) =>
        offerFlowTypes.includes(record.flowType as typeof offerFlowTypes[number]),
      );

    return hasNewerOfferActivity ? null : cancelledRecord;
  }

  get latestComplementaryBillingFeedback(): BillingRecordSummary | null {
    return (
      this.billingRecords.find(
        (record) =>
          record.flowType === "complementary_journeys" &&
          record.status === "cancelled"
      ) ?? null
    );
  }

  get subscriptionsHistory(): OrganizationSubscription[] {
    return [...(this.organization?.subscriptions ?? [])].sort(
      (subscriptionA, subscriptionB) =>
        new Date(subscriptionB.startsAt).getTime() -
        new Date(subscriptionA.startsAt).getTime()
    );
  }

  get selectedManagedPlan(): SubscriptionPlanCatalogItem | undefined {
    return this.managedPlansByName[this.managedSubscriptionPlanControl.value];
  }

  get selectedManagedPlanCode(): OfferPlanCode | undefined {
    return this.selectedManagedPlan?.slug as OfferPlanCode | undefined;
  }

  get selectedManagedPlanVersionId(): string | undefined {
    const selectedPlan = this.selectedManagedPlan;
    if (!selectedPlan?.versions?.length) {
      return undefined;
    }

    return [...selectedPlan.versions].sort(
      (versionA, versionB) =>
        new Date(versionB.effectiveFrom).getTime() -
        new Date(versionA.effectiveFrom).getTime()
    )[0]?.id;
  }

  get selectedSeatCount(): number {
    return this.managedOfferCheckoutService.normalizePositiveNumber(
      this.managedSubscriptionSeatCountControl.value,
      1
    );
  }

  get selectedAnnualPricePerSeatCents(): number {
    return this.managedOfferCheckoutService.getAnnualPricePerSeatCents(
      this.selectedManagedPlanCode,
      this.selectedSeatCount,
      this.selectedManagedPlanLatestVersion,
    );
  }

  get selectedAnnualAmountCents(): number {
    return this.selectedAnnualPricePerSeatCents * this.selectedSeatCount;
  }

  get selectedIncludedJourneysCount(): number {
    return this.managedOfferCheckoutService.getIncludedJourneysCount(
      this.selectedManagedPlanLatestVersion,
      this.selectedSeatCount,
      this.organization
    );
  }

  get selectedComplementaryJourneyPriceCents(): number {
    return this.managedOfferCheckoutService.getComplementaryJourneyPriceCents(
      this.selectedManagedPlanLatestVersion,
      this.organization
    );
  }

  get selectedOfferNextRenewalLabel(): string {
    return this.formatDate(
      this.managedOfferCheckoutService.getNextRenewalDate(
        this.managedSubscriptionStartsAtControl.value
      )
    );
  }

  get currentOfferExpiryLabel(): string {
    return this.formatDate(
      this.organization?.activeSubscription?.nextRenewalAt
    );
  }

  get canSaveManagedOffer(): boolean {
    return (
      !!this.selectedManagedPlanVersionId &&
      this.selectedSeatCount > 0 &&
      (!this.hasActiveManagedOffer || this.isAdminViewing) &&
      (!this.isStartDateInFuture || this.isAdminViewing) &&
      !this.isOfferSelectionUnchanged
    );
  }

  get offerActionLabel(): string {
    if (this.hasActiveManagedOffer && this.isAdminViewing) {
      return "Confirmer la mise à jour de mon offre";
    }

    return "Confirmer l'activation";
  }

  get canRequestSubscriptionChange(): boolean {
    return (
      this.hasActiveManagedOffer &&
      !this.isAdminViewing &&
      !this.activeSubscriptionChangeRequest
    );
  }

  get canReviewSubscriptionChangeRequest(): boolean {
    return this.isAdminViewing && this.activeSubscriptionChangeRequest?.status === "pending_review";
  }

  get canCancelActiveSubscriptionChangeRequest(): boolean {
    return !!this.activeSubscriptionChangeRequest
      && !this.isAdminViewing
      && ["pending_review", "payment_pending", "scheduled"].includes(
        this.activeSubscriptionChangeRequest.status,
      );
  }

  get cancelActiveSubscriptionChangeRequestLabel(): string {
    const request = this.activeSubscriptionChangeRequest;
    if (!request) {
      return "Annuler cette demande";
    }

    if (request.requestType === "cancellation_at_renewal") {
      return "Annuler la résiliation";
    }

    if (request.status === "pending_review") {
      return "Retirer cette demande";
    }

    return "Annuler cette demande";
  }

  get canSimulateSelectedUpgrade(): boolean {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    return !!currentPlanCode
      && !!this.selectedManagedPlanCode
      && this.comparePlanOrder(this.selectedManagedPlanCode, currentPlanCode) > 0
      && this.selectedSeatCount >= (this.organization?.activeSubscription?.seatCount ?? 0);
  }

  get canSimulateSelectedDowngrade(): boolean {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    return !!currentPlanCode
      && !!this.selectedManagedPlanCode
      && this.comparePlanOrder(this.selectedManagedPlanCode, currentPlanCode) < 0
      && this.selectedSeatCount >= (this.organization?.activeSubscription?.seatCount ?? 0);
  }

  get canSimulateSeatIncrease(): boolean {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    const currentSeatCount = this.organization?.activeSubscription?.seatCount ?? 0;

    return !!currentPlanCode
      && this.selectedManagedPlanCode === currentPlanCode
      && this.selectedSeatCount > currentSeatCount;
  }

  get canSimulateSeatDecrease(): boolean {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    const currentSeatCount = this.organization?.activeSubscription?.seatCount ?? 0;

    return !!currentPlanCode
      && this.selectedManagedPlanCode === currentPlanCode
      && this.selectedSeatCount < currentSeatCount;
  }

  get activeSubscriptionChangeStatusLabel(): string {
    const request = this.activeSubscriptionChangeRequest;
    if (!request) {
      return "Aucune demande en cours";
    }

    switch (request.status) {
      case "pending_review":
        return "En validation";
      case "payment_pending":
        return "Paiement requis";
      case "scheduled":
        return "Programmé";
      case "applied":
        return "Appliqué";
      case "cancelled":
        return "Annulé";
      case "rejected":
        return "Refusé";
      default:
        return "Préparation en cours";
    }
  }

  get activeSubscriptionChangeDescription(): string {
    const request = this.activeSubscriptionChangeRequest;
    if (!request) {
      return "Aucune demande d’évolution ou de résiliation n’est en attente.";
    }

    if (request.requestType === "cancellation_at_renewal") {
      return request.status === "pending_review"
        ? `Résiliation demandée pour le ${this.formatDate(request.effectiveAt)} après validation métier.`
        : `Résiliation prévue au ${this.formatDate(request.effectiveAt)}.`;
    }

    const targetPlan = request.targetPlanCode
      ? this.getPlanDisplayName(request.targetPlanCode as OfferPlanCode)
      : this.currentOfferName;
    const targetSeats = request.targetSeatCount ?? request.currentSeatCount;
    const amountLabel = request.estimatedAmountCents > 0
      ? this.formatCurrency(request.estimatedAmountCents)
      : "Sans paiement immédiat";

    const summary = `${targetPlan} • ${targetSeats} collaborateur${targetSeats > 1 ? "s" : ""} • ${amountLabel}`;
    return request.status === "pending_review"
      ? `${summary} • En attente de validation métier.`
      : summary;
  }

  get isStartDateInFuture(): boolean {
    return (
      new Date(this.managedSubscriptionStartsAtControl.value).getTime() >
      new Date(this.getTodayDate()).getTime()
    );
  }

  get selectedComplementaryJourneyQuantity(): number {
    return this.managedOfferCheckoutService.normalizePositiveNumber(
      this.complementaryJourneysQuantityControl.value,
      1
    );
  }

  get complementaryJourneyTypeOptions(): string[] {
    return ["Parole en groupe", "Coaching en groupe"];
  }

  get selectedComplementaryJourneyType(): DynamicType | undefined {
    if (!this.shouldChooseComplementaryJourneyType) {
      return undefined;
    }

    return this.complementaryJourneyTypeControl.value === "Coaching en groupe"
      ? DynamicType.COACHING
      : DynamicType.SPEECH;
  }

  get currentComplementaryJourneyPriceCents(): number {
    return this.managedOfferCheckoutService.getCurrentComplementaryJourneyPriceCents(
      this.organization
    );
  }

  get currentLegacyAnnualSubscriptionPriceCents(): number | null {
    return (
      this.organization?.capabilities?.credit_policy?.commercial_terms
        ?.annual_subscription_price_cents ?? null
    );
  }

  get currentLegacySpeechJourneyPriceCents(): number | null {
    return (
      this.organization?.capabilities?.credit_policy?.commercial_terms
        ?.journey_price_cents ?? null
    );
  }

  get currentLegacyCoachingJourneyPriceCents(): number | null {
    return (
      this.organization?.capabilities?.credit_policy?.commercial_terms
        ?.coaching_journey_price_cents ?? null
    );
  }

  get selectedComplementaryJourneyUnitPriceCents(): number {
    if (!this.shouldChooseComplementaryJourneyType) {
      return this.currentComplementaryJourneyPriceCents;
    }

    return this.selectedComplementaryJourneyType === DynamicType.COACHING
      ? this.currentLegacyCoachingJourneyPriceCents ??
          this.currentComplementaryJourneyPriceCents
      : this.currentLegacySpeechJourneyPriceCents ??
          this.currentComplementaryJourneyPriceCents;
  }

  get complementaryJourneysTotalCents(): number {
    return (
      this.selectedComplementaryJourneyUnitPriceCents *
      this.selectedComplementaryJourneyQuantity
    );
  }

  get canPurchaseComplementaryJourneys(): boolean {
    return (
      !!this.organization?.activeSubscription &&
      this.selectedComplementaryJourneyQuantity > 0 &&
      this.selectedComplementaryJourneyUnitPriceCents > 0
    );
  }

  get selectedManagedOfferName(): string {
    return this.selectedManagedPlanCode
      ? this.getPlanDisplayName(this.selectedManagedPlanCode)
      : "Aucune offre sélectionnée";
  }

  get selectedManagedPlanLatestVersion() {
    return this.managedOfferCheckoutService.resolveCatalogPlanVersion(
      this.selectedManagedPlanCode,
      this.selectedManagedPlan,
    );
  }

  private syncOfferControls(): void {
    if (this.loadingCatalog || !this.organization) {
      return;
    }

    const currentPlanCode = this.organization.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    const selectedPlanCode =
      currentPlanCode && this.managedFormulaCodes.includes(currentPlanCode)
        ? currentPlanCode
        : this.managedFormulaCodes[0];

    if (!this.managedSubscriptionPlanControl.value) {
      this.managedSubscriptionPlanControl.setValue(
        this.getPlanDisplayName(selectedPlanCode)
      );
    } else if (
      !this.managedPlansByName[this.managedSubscriptionPlanControl.value]
    ) {
      this.managedSubscriptionPlanControl.setValue(
        this.getPlanDisplayName(selectedPlanCode)
      );
    }

    if (this.organization.activeSubscription?.startsAt) {
      this.managedSubscriptionStartsAtControl.setValue(
        new Date(this.organization.activeSubscription.startsAt)
          .toISOString()
          .split("T")[0]
      );
    } else {
      this.managedSubscriptionStartsAtControl.setValue(
        this.managedOfferCheckoutService.getTodayDate()
      );
    }

    this.managedSubscriptionSeatCountControl.setValue(
      this.organization.activeSubscription?.seatCount ??
        Math.max(this.organization.members?.length ?? 0, 1)
    );
  }

  private buildTransactions(): void {
    const unitsIn = this.organization.unitsIn ?? [];
    unitsIn?.forEach((transaction) => {
      transaction.receiver = this.organization;
    });
    const unitsOut = this.organization.unitsOut ?? [];
    unitsOut?.forEach((transaction) => {
      transaction.sender = this.organization;
    });

    let restAmount = this.displayedUnits;
    const transactions =
      unitsIn
        .concat(...unitsOut)
        ?.sort(
          (transactionA, transactionB) =>
            transactionB.createdAt.getTime() - transactionA.createdAt.getTime()
        )
        ?.map((transaction): OrganizationTransactionParsed => {
          const organizationIsSender =
            !!transaction.sender &&
            transaction.sender.id === this.organization.id;
          const stepAmount = restAmount;

          restAmount = organizationIsSender
            ? restAmount + transaction.units
            : restAmount - transaction.units;

          return {
            id: transaction.id,
            date: transaction.createdAt,
            units: organizationIsSender
              ? `-${transaction.units}`
              : `+${transaction.units}`,
            restAmount: stepAmount,
            description: this.getTransactionActionNature(
              transaction,
              organizationIsSender
            ),
          };
        })
        ?.sort((transactionA, transactionB) => {
          const sortOption = this.sortOptionChosen$.getValue();

          if (sortOption?.sort === "date") {
            return (
              (transactionA.date.getTime() - transactionB.date.getTime()) *
              (sortOption.order === "DESC" ? -1 : 1)
            );
          }
          if (sortOption?.sort === "description") {
            return transactionA.description.toLowerCase() <
              transactionB.description.toLowerCase()
              ? -1 * (sortOption.order === "DESC" ? -1 : 1)
              : sortOption.order === "DESC"
              ? -1
              : 1;
          }
          return transactionB.date.getTime() - transactionA.date.getTime();
        }) ?? [];

    this.transactions = transactions;
  }

  private getTransactionActionNature(
    transaction: Transaction,
    organizationIsSender: boolean
  ): string {
    const isPlural = Math.abs(transaction.units) > 1;

    switch (transaction.type) {
      case TransactionType.ORGANIZATION_TRANSFER:
        if (organizationIsSender) {
          return `Transfert de crédit${
            isPlural ? "s" : ""
          } vers l'organisation (${transaction.receiver?.name?.toUpperCase()})`;
        }
        return `Transfert de crédit${
          isPlural ? "s" : ""
        } depuis l'organisation (${transaction.sender?.name?.toUpperCase()})`;
      case TransactionType.ADMIN_MODIFICATION:
        if (organizationIsSender) {
          return `Retrait de crédit${
            isPlural ? "s" : ""
          } par administrateur We talk`;
        }
        return `Ajout de crédit${
          isPlural ? "s" : ""
        } par administrateur We talk`;
      case TransactionType.COMPLEMENTARY_JOURNEY_PURCHASE: {
        const journeyCount = this.getJourneyCountFromUnits(transaction.units);
        return `Achat de ${journeyCount} parcours complémentaire${
          journeyCount > 1 ? "s" : ""
        }`;
      }
      case TransactionType.TRAIN_MEMBERSHIP:
        if (organizationIsSender) {
          return `Inscription ${isPlural ? "à des" : "à un"} parcours`;
        }
        return `Remboursement ${isPlural ? "des" : "d'un"} parcours annulé${
          isPlural ? "s" : ""
        }`;
      case TransactionType.ORIENTATION_CALL:
        return `Appel d'orientation`;
      case TransactionType.SPEECH_TRAIN_MEMBERSHIP:
        if (organizationIsSender) {
          return `Inscription à un parcours de Parole`;
        }
        return `Remboursement d'un parcours de Parole annulé`;
      case TransactionType.COACHING_TRAIN_MEMBERSHIP:
        if (organizationIsSender) {
          return `Inscription à un parcours de Coaching`;
        }
        return `Remboursement d'un parcours de Coaching annulé`;
      case TransactionType.FORMATION_MEMBERSHIP:
        return `Formation d'auto-coaching`;
      default:
        return "Nature inconnue";
    }
  }

  private getJourneyCountFromUnits(units: number): number {
    const unitsPerJourney =
      this.organization?.creditSummary?.journeyCreditsCost ??
      this.organization?.creditCosts?.coachingTrain ??
      10;

    return Math.max(
      1,
      Math.round(Math.abs(units) / Math.max(1, unitsPerJourney))
    );
  }

  private getPlanDisplayName(planCode: OfferPlanCode): string {
    return this.managedOfferCheckoutService.getPlanDisplayName(planCode);
  }

  private comparePlanOrder(left: OfferPlanCode, right: OfferPlanCode): number {
    return (this.planOrder.get(left) ?? -1) - (this.planOrder.get(right) ?? -1);
  }

  getSubscriptionDisplayName(subscription: OrganizationSubscription): string {
    const planCode = subscription.planVersion?.plan?.slug as
      | OfferPlanCode
      | undefined;
    const isManagedOfferPlan =
      !!planCode && this.managedFormulaCodes.includes(planCode);

    return isManagedOfferPlan
      ? this.getPlanDisplayName(planCode)
      : this.getLegacyPlanDisplayName(
          subscription.planVersion?.plan?.slug,
          subscription.planVersion?.plan?.name
        ) ??
          subscription.planVersion?.versionCode ??
          "Offre";
  }

  private getLegacyPlanDisplayName(
    planSlug?: string,
    fallbackName?: string | null
  ): string {
    return planSlug
      ? SubscriptionPlanMetadata[planSlug]?.name ?? fallbackName ?? ""
      : fallbackName ?? "";
  }

  private getJourneyTypeLabel(journeyType?: DynamicType | null): string | null {
    if (journeyType === DynamicType.SPEECH) {
      return "Parole";
    }

    if (journeyType === DynamicType.COACHING) {
      return "Coaching";
    }

    return null;
  }

  getBillingRecordFeedbackTitle(record: BillingRecordSummary): string {
    const title = this.getBillingRecordTitle(record);

    if (this.isRefundedBillingRecord(record)) {
      return `${title} remboursé${record.seatCount && record.seatCount > 1 ? "e" : ""}`;
    }

    return `${title} annulée`;
  }

  getBillingRecordTitle(record: BillingRecordSummary): string {
    switch (record.flowType) {
      case "subscription_initial":
        return "Activation d’offre";
      case "subscription_renewal":
        return "Renouvellement d’offre";
      case "complementary_journeys":
        return this.getJourneyTypeLabel(record.journeyType)
          ? `Achat de parcours ${this.getJourneyTypeLabel(record.journeyType)}`
          : "Achat de parcours complémentaires";
      case "subscription_upgrade":
        return "Montée en gamme immédiate";
      case "seat_increase":
        return "Augmentation du nombre de collaborateurs";
      default:
        return "Facturation Sellsy";
    }
  }

  getBillingRecordStatusLabel(record: BillingRecordSummary): string {
    if (this.isRefundedBillingRecord(record)) {
      return "Avoir appliqué";
    }

    switch (record.status) {
      case "payment_pending":
        return "Paiement en attente";
      case "paid":
        return "Paiement confirmé";
      case "synced":
        return "Confirmé";
      case "failed":
        return "Paiement échoué";
      case "cancelled":
        return "Annulé";
      case "draft":
        return "Brouillon";
      default:
        return "Inconnu";
    }
  }

  getBillingRecordStatusTone(
    record: BillingRecordSummary
  ): "success" | "warning" | "danger" | "neutral" {
    if (this.isRefundedBillingRecord(record)) {
      return "warning";
    }

    switch (record.status) {
      case "synced":
      case "paid":
        return "success";
      case "payment_pending":
      case "draft":
        return "warning";
      case "failed":
        return "danger";
      default:
        return "neutral";
    }
  }

  getBillingRecordSummary(record: BillingRecordSummary): string {
    if (this.isRefundedBillingRecord(record)) {
      return "Un avoir ou un remboursement Sellsy a été synchronisé. We talk a remis l’offre ou les crédits associés à jour.";
    }

    switch (record.status) {
      case "payment_pending":
        return "Le lien de paiement reste réutilisable tant qu’un nouveau flux n’a pas été lancé.";
      case "synced":
        return "Le paiement a bien été confirmé et votre accès mis à jour.";
      case "failed":
        return "Le paiement a échoué côté Sellsy. Relancez un paiement si nécessaire.";
      case "cancelled":
        return "La demande a été annulée avant la confirmation du paiement.";
      case "paid":
        return "Le paiement a été confirmé côté Sellsy et attend la synchronisation finale.";
      case "draft":
        return "Le document existe côté facturation mais n’est pas encore finalisé.";
      default:
        return "Consultez Sellsy pour plus de détails sur ce document.";
    }
  }

  getBillingRecordPrimaryDate(record: BillingRecordSummary): string {
    return this.formatDate(
      record.activatedAt ??
        record.paidAt ??
        record.issuedAt ??
        record.periodStartAt ??
        undefined
    );
  }

  getBillingRecordMeta(record: BillingRecordSummary): string[] {
    const items = [
      record.amountCents ? this.formatCurrency(record.amountCents) : null,
      record.seatCount
        ? `${record.seatCount} collaborateur${record.seatCount > 1 ? "s" : ""}`
        : null,
      record.quantity ? `${record.quantity} parcours` : null,
      this.getJourneyTypeLabel(record.journeyType),
      record.providerDocumentId ? `Pièce ${record.providerDocumentId}` : null,
    ];

    return items.filter((item): item is string => !!item);
  }

  trackBillingRecord(_index: number, record: BillingRecordSummary): string {
    return record.id;
  }

  private isRefundedBillingRecord(record: BillingRecordSummary): boolean {
    return (
      record.status === "cancelled" &&
      (record.errorMessage === "sellsy_refund_applied" ||
        !!record.activatedAt ||
        !!record.paidAt)
    );
  }

  private formatDate(date?: Date | string | null): string {
    return date ? new Date(date).toLocaleDateString() : "Non disponible";
  }

  private getTodayDate(): string {
    return this.managedOfferCheckoutService.getTodayDate();
  }

  formatCurrency(amountCents: number): string {
    return this.managedOfferCheckoutService.formatCurrency(amountCents);
  }

  private get isOfferSelectionUnchanged(): boolean {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as
      | OfferPlanCode
      | undefined;
    const currentStartDate = this.organization?.activeSubscription?.startsAt
      ? new Date(this.organization.activeSubscription.startsAt)
          .toISOString()
          .split("T")[0]
      : undefined;
    const currentSeatCount = this.organization?.activeSubscription?.seatCount;

    return (
      !!this.organization?.activeSubscription &&
      currentPlanCode === this.selectedManagedPlanCode &&
      currentStartDate === this.managedSubscriptionStartsAtControl.value &&
      currentSeatCount === this.selectedSeatCount
    );
  }

  onSimulateUpgrade() {
    if (!this.selectedManagedPlanCode) {
      return;
    }

    this.requestSubscriptionChange({
      requestType: "upgrade_now",
      targetPlanCode: this.selectedManagedPlanCode,
      targetSeatCount: this.selectedSeatCount,
    });
  }

  onSimulateDowngrade() {
    if (!this.selectedManagedPlanCode) {
      return;
    }

    this.requestSubscriptionChange({
      requestType: "downgrade_at_renewal",
      targetPlanCode: this.selectedManagedPlanCode,
      targetSeatCount: this.selectedSeatCount,
    });
  }

  onSimulateSeatIncrease() {
    this.requestSubscriptionChange({
      requestType: "seat_increase_now",
      targetSeatCount: this.selectedSeatCount,
    });
  }

  onSimulateSeatDecrease() {
    this.requestSubscriptionChange({
      requestType: "seat_decrease_at_renewal",
      targetSeatCount: this.selectedSeatCount,
    });
  }

  onScheduleCancellation() {
    this.requestSubscriptionChange({
      requestType: "cancellation_at_renewal",
    });
  }

  cancelActiveSubscriptionChangeRequest() {
    const request = this.activeSubscriptionChangeRequest;
    if (!request?.id) {
      return;
    }

    const message = request.requestType === "cancellation_at_renewal"
      ? "La résiliation programmée sera retirée et le workflow redeviendra disponible."
      : "Cette demande d'abonnement sera annulée et le workflow redeviendra disponible.";

    this.dialogService.open(ConfirmComponent, {
      title: "Confirmer cette annulation ?",
      message,
      confirmMessage: "Confirmer",
      cancelMessage: "Retour",
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((isConfirmed) => {
        if (!isConfirmed) {
          return;
        }

        this.performSubscriptionChangeCancellation(request.id, request.requestType);
      });
  }

  approveActiveSubscriptionChangeRequest() {
    const request = this.activeSubscriptionChangeRequest;
    if (!request?.id) {
      return;
    }

    this.dialogService.open(ConfirmComponent, {
      title: "Approuver cette demande ?",
      message: "La demande passera ensuite en paiement requis, en programmation à échéance ou sera appliquée immédiatement selon son type.",
      confirmMessage: "Approuver",
      cancelMessage: "Annuler",
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((isConfirmed) => {
        if (!isConfirmed) {
          return;
        }

        this.reviewSubscriptionChangeRequest("approve", request.id);
      });
  }

  rejectActiveSubscriptionChangeRequest() {
    const request = this.activeSubscriptionChangeRequest;
    if (!request?.id) {
      return;
    }

    this.dialogService.open(ConfirmComponent, {
      title: "Refuser cette demande ?",
      message: "La demande sera marquée comme refusée et ne sera pas exécutée.",
      confirmMessage: "Refuser",
      cancelMessage: "Annuler",
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((isConfirmed) => {
        if (!isConfirmed) {
          return;
        }

        this.reviewSubscriptionChangeRequest("reject", request.id);
      });
  }

  openCheckout(url?: string | null) {
    if (!url) {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }

  copyCheckoutUrl(url?: string | null) {
    if (!url) {
      return;
    }

    this.copyTextToClipboard(url).then((isCopied) => {
      if (isCopied) {
        this.snackBarService.pushMessage(
          "Le lien de paiement a été copié",
          "success"
        );
        return;
      }

      this.snackBarService.pushMessage(
        "Impossible de copier le lien de paiement",
        "error"
      );
    });
  }

  private requestSubscriptionChange(payload: {
    requestType: SubscriptionChangeType;
    requestedEffectiveMode?: SubscriptionChangeEffectiveMode;
    targetPlanCode?: string;
    targetSeatCount?: number;
  }): void {
    if (!this.organization?.id || this.isSubmittingSubscriptionChange) {
      return;
    }

    if (this.requiresTimingChoice(payload) && !payload.requestedEffectiveMode) {
      this.openSubscriptionChangeTimingChoice(payload);
      return;
    }

    this.isSubmittingSubscriptionChange = true;
    this.organizationRepository
      .simulateSubscriptionChange(this.organization.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (simulation) => {
          this.isSubmittingSubscriptionChange = false;
          this.confirmSubscriptionChange(payload, simulation);
        },
        error: () => {
          this.isSubmittingSubscriptionChange = false;
          this.snackBarService.pushMessage(
            "Impossible de préparer ce changement d'abonnement",
            "error"
          );
        },
      });
  }

  private confirmSubscriptionChange(
    payload: SubscriptionChangeRequestPayload,
    simulation: SubscriptionChangeSimulation
  ): void {
    const targetPlanName = simulation.targetPlanCode
      ? this.getPlanDisplayName(simulation.targetPlanCode as OfferPlanCode)
      : "résiliation à échéance";
    const amountLabel = simulation.estimatedAmountCents > 0
      ? this.formatCurrency(simulation.estimatedAmountCents)
      : "Sans paiement immédiat";
    const effectLabel = this.formatDate(simulation.effectiveAt);

    this.dialogService.open(ConfirmComponent, {
      title: "Confirmer cette demande d'abonnement ?",
      message: `${targetPlanName} • ${simulation.targetSeatCount ?? simulation.currentSeatCount} collaborateur(s) • ${amountLabel} • Effet ${simulation.effectiveMode === "immediate" ? "immédiat" : "au renouvellement"} (${effectLabel}).`,
      confirmMessage: "Confirmer",
      cancelMessage: "Annuler",
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((isConfirmed) => {
        if (!isConfirmed) {
          return;
        }

        this.createSubscriptionChangeRequest(payload);
      });
  }

  private createSubscriptionChangeRequest(payload: {
    requestType: SubscriptionChangeType;
    requestedEffectiveMode?: SubscriptionChangeEffectiveMode;
    targetPlanCode?: string;
    targetSeatCount?: number;
  }): void {
    if (!this.organization?.id) {
      return;
    }

    this.isSubmittingSubscriptionChange = true;
    this.organizationRepository
      .createSubscriptionChangeRequest(this.organization.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmittingSubscriptionChange = false;

          if (response.checkoutUrl) {
            this.openCheckout(response.checkoutUrl);
            this.snackBarService.pushMessage(
              "Le lien de paiement pour cette évolution a été ouvert",
              "success"
            );
          } else if (response.status === "pending_review") {
            this.snackBarService.pushMessage(
              "La demande a été transmise pour validation métier",
              "success"
            );
          } else if (response.status === "applied") {
            this.snackBarService.pushMessage(
              "La demande a été appliquée",
              "success"
            );
            this.organizationStoreService
              .reloadOrganization()
              .pipe(takeUntil(this.destroy$))
              .subscribe();
          } else {
            this.snackBarService.pushMessage(
              "La demande a été programmée",
              "success"
            );
          }

          this.loadBillingStatus(this.organization.id);
        },
        error: () => {
          this.isSubmittingSubscriptionChange = false;
          this.snackBarService.pushMessage(
            "Impossible d'enregistrer cette demande d'abonnement",
            "error"
          );
        },
      });
  }

  private requiresTimingChoice(payload: {
    requestType: SubscriptionChangeType;
    requestedEffectiveMode?: SubscriptionChangeEffectiveMode;
  }): boolean {
    return (
      (payload.requestType === "upgrade_now" || payload.requestType === "seat_increase_now")
      && !payload.requestedEffectiveMode
    );
  }

  private openSubscriptionChangeTimingChoice(payload: {
    requestType: SubscriptionChangeType;
    targetPlanCode?: string;
    targetSeatCount?: number;
  }): void {
    const isUpgrade = payload.requestType === "upgrade_now";
    const noun = isUpgrade ? "cette montée en gamme" : "cette hausse de collaborateurs";

    this.dialogService.open(ConfirmComponent, {
      title: "Quand souhaitez-vous appliquer ce changement ?",
      message: `Vous pouvez payer maintenant pour appliquer ${noun} immédiatement,\nou la programmer à la date anniversaire de l'abonnement.`,
      confirmMessage: "Payer maintenant",
      cancelMessage: "Programmer au renouvellement",
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((isImmediate) => {
        if (typeof isImmediate !== "boolean") {
          return;
        }

        this.requestSubscriptionChange({
          ...payload,
          requestedEffectiveMode: isImmediate ? "immediate" : "next_renewal",
        });
      });
  }

  private reviewSubscriptionChangeRequest(
    action: "approve" | "reject",
    requestId: string,
  ): void {
    if (!this.organization?.id || this.isSubmittingSubscriptionChange) {
      return;
    }

    this.isSubmittingSubscriptionChange = true;
    const review$ = action === "approve"
      ? this.organizationRepository.approveSubscriptionChangeRequest(
          this.organization.id,
          requestId,
        )
      : this.organizationRepository.rejectSubscriptionChangeRequest(
          this.organization.id,
          requestId,
        );

    review$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmittingSubscriptionChange = false;

          if (response.status === "payment_pending") {
            this.snackBarService.pushMessage(
              "La demande a été approuvée et attend désormais le paiement",
              "success"
            );
          } else if (response.status === "scheduled") {
            this.snackBarService.pushMessage(
              "La demande a été approuvée et programmée",
              "success"
            );
          } else if (response.status === "applied") {
            this.snackBarService.pushMessage(
              "La demande a été approuvée et appliquée",
              "success"
            );
            this.organizationStoreService
              .reloadOrganization()
              .pipe(takeUntil(this.destroy$))
              .subscribe();
          } else {
            this.snackBarService.pushMessage(
              "La demande a été refusée",
              "success"
            );
          }

          this.loadBillingStatus(this.organization.id);
        },
        error: () => {
          this.isSubmittingSubscriptionChange = false;
          this.snackBarService.pushMessage(
            action === "approve"
              ? "Impossible d'approuver cette demande"
              : "Impossible de refuser cette demande",
            "error"
          );
        },
      });
  }

  private performSubscriptionChangeCancellation(
    requestId: string,
    requestType: SubscriptionChangeType,
  ): void {
    if (!this.organization?.id || this.isSubmittingSubscriptionChange) {
      return;
    }

    this.isSubmittingSubscriptionChange = true;
    this.organizationRepository
      .cancelSubscriptionChangeRequest(this.organization.id, requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmittingSubscriptionChange = false;
          this.snackBarService.pushMessage(
            requestType === "cancellation_at_renewal"
              ? "La résiliation programmée a été annulée"
              : "La demande a été annulée",
            "success"
          );
          this.loadBillingStatus(this.organization.id);
        },
        error: () => {
          this.isSubmittingSubscriptionChange = false;
          this.snackBarService.pushMessage(
            "Impossible d'annuler cette demande",
            "error"
          );
        },
      });
  }

  private handleSubscriptionCheckoutResponse(
    response: BillingCheckoutResponse
  ) {
    if (response.checkoutUrl) {
      this.openCheckout(response.checkoutUrl);
      this.snackBarService.pushMessage(
        "Le lien de paiement Sellsy a été ouvert",
        "success"
      );
      this.loadBillingStatus(this.organization.id);
      return;
    }

    this.organizationStoreService
      .reloadOrganization()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBarService.pushMessage(
            "Votre offre a bien été activée",
            "success"
          );
          this.loadBillingStatus(this.organization.id);
        },
        error: () => {
          this.snackBarService.pushMessage(
            "L'offre a été activée, mais la page n'a pas pu être actualisée",
            "default"
          );
          this.loadBillingStatus(this.organization.id);
        },
      });
  }

  private handleComplementaryJourneysCheckoutResponse(
    response: BillingCheckoutResponse
  ) {
    if (response.checkoutUrl) {
      this.openCheckout(response.checkoutUrl);
      this.snackBarService.pushMessage(
        "Le lien de paiement Sellsy a été ouvert",
        "success"
      );
      this.loadBillingStatus(this.organization.id);
      return;
    }

    this.snackBarService.pushMessage(
      "Le checkout Sellsy n'a pas retourné de lien de paiement",
      "error"
    );
    this.loadBillingStatus(this.organization.id);
  }

  private loadBillingStatus(organizationId: string) {
    this.organizationRepository
      .getBillingStatus(organizationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (billingStatus) => {
          this.billingStatus = billingStatus;
        },
        error: () => {
          this.billingStatus = null;
        },
      });
  }

  private async copyTextToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      console.error("Clipboard API copy failed:", error);
    }

    return this.copyTextWithExecCommand(text);
  }

  private copyTextWithExecCommand(text: string): boolean {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "true");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.style.pointerEvents = "none";

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const isCopied = document.execCommand("copy");
      document.body.removeChild(textarea);
      return isCopied;
    } catch (error) {
      console.error("Fallback copy failed:", error);
      return false;
    }
  }
}
